/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import colors from '@sportninja/common/constants/appColors'
import cn from 'classnames'
import { ROUTES } from '@sportninja/common/constants/app'

import Select from '../../../components/Select'
import DatePicker from '../../../components/DatePicker'
import { Flex } from '../../../components/Layout'
import Picture from '../../../components/Picture'
import Icon from '../../../components/Icon'
import SuspensionSubmitButton from '../../../components/Suspensions/SuspensionSubmitButton'
import {
  GAME_SLOT_STATUS,
  TOURNAMENT_STATUS,
} from '../ScheduleBracket/constants'
import { IS_DEVELOPMENT } from '../../../utils/utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import {
  setTimeToBeDefined,
  isTimeToBeDefined,
} from '@sportninja/common/ts-utils/date-utils'
import { useTimezoneManager } from '@sportninja/common/hooks/useTimezoneManager'
import { Tooltip } from 'antd'

const NewGameTeamSlot = ({
  homeDidWin,
  visitingDidWin,
  homeImageId,
  visitingImageId,
  homeTeamName,
  visitingTeamName,
  isEnded,
  homeTeamScore = 0,
  visitingTeamScore = 0,
  disabled = false,
  onPressTeamModal = () => {},
  game,
}) => {
  // team_slot_type_id === 2 is an unresolved PLACE_HOLDER ("Winner of Game 1",
  // "1st in Division", etc.) that advancement/seed resolution fills automatically.
  // Inlined to match the existing `team_slot_type_id !== 2` usage in ScheduleBracket/Slot.jsx.
  const PLACE_HOLDER_SLOT_TYPE = 2
  const homeIsPlaceholder =
    game?.homeTeamSlot?.team_slot_type_id === PLACE_HOLDER_SLOT_TYPE
  const visitingIsPlaceholder =
    game?.visitingTeamSlot?.team_slot_type_id === PLACE_HOLDER_SLOT_TYPE
  const placeholderTooltip =
    'Filled automatically when the feeding game completes'
  return (
    <td className='team'>
      <div
        css={css`
          margin-top: 2px;
          margin-bottom: 2px;
        `}
      >
        <Tooltip title={homeIsPlaceholder ? placeholderTooltip : ''}>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: flex-start;
              gap: 8px;
              cursor: ${homeIsPlaceholder ? 'default' : 'pointer'};
            `}
            onClick={() => {
              if (homeIsPlaceholder) {
                return
              }
              const data = {
                game: game,
                isHome: true,
                ...game?.homeTeamSlot,
              }
              onPressTeamModal(data)
            }}
            onMouseEnter={() => {
              if (homeIsPlaceholder) {
                return
              }
              const element = document.getElementById(
                `home-team-name-${game?.id}`
              )
              if (element) {
                element.style.color = colors.PRIMARY
              }
            }}
            onMouseLeave={() => {
              const element = document.getElementById(
                `home-team-name-${game?.id}`
              )
              if (element) {
                element.style.color = homeDidWin
                  ? 'var(--Neutrals-White, #fff)'
                  : 'var(--Neutrals-White, #fff)'
              }
            }}
          >
            <Picture
              square
              size='xxxsmall'
              imageId={homeImageId}
              css={css`
                margin: 6px;
              `}
              iconName='user-friends'
            />
            <div
              css={css`
                display: flex;
                flex-direction: column;
                width: 160px;
              `}
            >
              <span
                id={`home-team-name-${game?.id}`}
                css={css`
                  color: #fff;
                  font-family: 'Maison Neue', sans-serif !important;
                  font-size: 16px;
                  font-style: normal;
                  font-weight: 500;
                  line-height: normal;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  max-width: 100%; // Adjust this value as needed
                  display: block; // Or inline-block, depending on your layout
                  /* text-decoration: ${'underline'}; */
                `}
              >
                {homeTeamName}
              </span>
              <span
                css={css`
                  overflow: hidden;
                  color: ${colors.ATTENDANCE_GRAY};
                  text-overflow: ellipsis;
                  font-family: 'Maison Neue', sans-serif !important;
                  font-size: 12px;
                  font-style: normal;
                  font-weight: 500;
                  line-height: normal;
                `}
              >
                Home
              </span>
            </div>
            <span
              css={css`
                color: ${homeDidWin
                  ? colors.PRIMARY
                  : 'var(--Neutrals-White, #fff)'};
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: 100%; /* 18px */
                letter-spacing: 0.9px;
                text-transform: uppercase;
              `}
            >
              {homeTeamScore?.toString()}
            </span>
          </div>
        </Tooltip>
        {/* Separator */}
        <div
          css={css`
            /* height: 2px; */
          `}
        />
        {/* Visiting Row */}
        <Tooltip title={visitingIsPlaceholder ? placeholderTooltip : ''}>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: flex-start;
              gap: 8px;
              cursor: ${visitingIsPlaceholder ? 'default' : 'pointer'};
            `}
            onClick={() => {
              if (visitingIsPlaceholder) {
                return
              }
              const data = {
                game: game,
                isHome: false,
                ...game?.visitingTeamSlot,
              }
              onPressTeamModal(data)
            }}
            onMouseEnter={() => {
              if (visitingIsPlaceholder) {
                return
              }
              const element = document.getElementById(
                `visiting-team-name-${game?.id}`
              )
              if (element) {
                element.style.color = colors.PRIMARY
              }
            }}
            onMouseLeave={() => {
              const element = document.getElementById(
                `visiting-team-name-${game?.id}`
              )
              if (element) {
                element.style.color = visitingDidWin
                  ? 'var(--Neutrals-White, #fff)'
                  : 'var(--Neutrals-White, #fff)'
              }
            }}
          >
            <Picture
              square
              size='xxxsmall'
              imageId={visitingImageId}
              css={css`
                margin: 6px;
              `}
              iconName='user-friends'
            />
            <div
              css={css`
                display: flex;
                flex-direction: column;
                width: 160px;
              `}
            >
              <span
                id={`visiting-team-name-${game?.id}`}
                css={css`
                  color: #fff;
                  font-family: 'Maison Neue', sans-serif !important;
                  font-size: 16px;
                  font-style: normal;
                  font-weight: 500;
                  line-height: normal;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  max-width: 100%; // Adjust this value as needed
                  display: block; // Or inline-block, depending on your layout
                  /* text-decoration: ${'underline'}; */
                `}
              >
                {visitingTeamName}
              </span>
              <span
                css={css`
                  overflow: hidden;
                  color: ${colors.ATTENDANCE_GRAY};
                  text-overflow: ellipsis;
                  font-family: 'Maison Neue', sans-serif !important;
                  font-size: 12px;
                  font-style: normal;
                  font-weight: 500;
                  line-height: normal;
                `}
              >
                Visitor
              </span>
            </div>
            <span
              css={css`
                color: ${visitingDidWin
                  ? colors.PRIMARY
                  : 'var(--Neutrals-White, #fff)'};
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: 100%; /* 18px */
                letter-spacing: 0.9px;
                text-transform: uppercase;
              `}
            >
              {visitingTeamScore?.toString()}
            </span>
          </div>
        </Tooltip>
      </div>
    </td>
  )
}

/**
 * Determines the title to display for a bracket round based on tournament type and round position.
 *
 * @param {boolean} isFinals - Whether the current round is the finals
 * @param {boolean} isSemifinals - Whether the current round is the semifinals
 * @param {boolean} isSingleElimination - Whether the tournament is single elimination
 * @param {boolean} isDoubleElimination - Whether the tournament is double elimination
 * @param {string} defaultTitle - The default title to display if no special case applies
 * @returns {string} - The formatted title to display for the current round
 */
const getBracketRoundTitle = (
  isFinals,
  isSemifinals,
  isSingleElimination,
  isDoubleElimination,
  defaultTitle
) => {
  if (isDoubleElimination) {
    if (isFinals) {
      return 'Finals (If Necessary)'
    } else if (isSemifinals) {
      return 'Finals'
    }
  }
  if (isFinals) {
    return 'FINALS'
  } else if (isSemifinals) {
    return 'SEMIFINALS'
  } else {
    return defaultTitle
  }
}

const GameSlot = ({
  availableVenues,
  form = {},
  handleSetForm,
  game,
  submitLoading,
  advanceLoading,
  onApprove,
  onDebugAdvance,
  idx,
  prevGameIsComplete,
  prevGameHasBye = false,
  tournamentStatus,
  onPressCopy,
  onPressTeamModal = () => {},
  isSingleOrDoubleTournament = false,
  isSingleElimination = false,
  isDoubleElimination = false,
  isFinals = false,
  isSemifinals = false,
}) => {
  const {
    id: gameId,
    game_id: SNGameUID,
    ended_at,
    game_number,
    starts_at,
    venue_id,
    facility_id,
    homeTeamSlot = {},
    visitingTeamSlot = {},
    round,
    pool,
    home_team_score,
    visiting_team_score,
    status,
    has_bye = false,
  } = game

  // Detect if the starts_at follows TBD pattern
  const detectedTDBStatus = starts_at ? isTimeToBeDefined(starts_at) : false

  const { DateTime } = useTimezoneManager({
    date: null,
    timezone: null,
  })

  const displayApprovalButton =
    ended_at && status === GAME_SLOT_STATUS.Ready_To_Play
  const advanceButtonsEnabled =
    (idx === 0 || prevGameIsComplete || prevGameHasBye) &&
    !ended_at &&
    status === GAME_SLOT_STATUS.Ready_To_Play &&
    tournamentStatus === TOURNAMENT_STATUS.IN_PROGRESS
  const isDirty = Object.keys(form).length > 0

  // Old approach
  // const roundName =
  //   round?.round_type_id > 1
  //     ? ROUND_TYPE[round?.round_type_id]
  //     : round?.name_full?.replace('Round ', '')

  // New approach
  const roundName = getBracketRoundTitle(
    isFinals,
    isSemifinals,
    isSingleElimination,
    isDoubleElimination,
    round?.name_full
  )
  const poolName = round?.round_type_id > 1 || !pool?.letter ? '-' : pool.letter
  const selectedVenue = form?.venue_id || game?.venue_id
  const selectedVenueObject = availableVenues.find(
    (v) => v.id === selectedVenue
  )

  const onChange = (key, { target }) => {
    if (key === 'starts_at') {
      const gameToBeDefined = form?.time_to_be_defined_status || false
      const value = target?.value

      if (value && gameToBeDefined && selectedVenueObject?.timezone) {
        const userTimeStartDate = DateTime.fromISO(value)
        const locationStartDate = userTimeStartDate.setZone(
          selectedVenueObject.timezone,
          {
            keepLocalTime: true,
          }
        )
        const finalValue = setTimeToBeDefined(locationStartDate.toUTC().toISO())
        handleSetForm(key, finalValue)
      } else {
        handleSetForm(key, value)
      }
    } else {
      handleSetForm(key, target?.value)
    }
  }

  const homeImageId = getImageThumbnailId(homeTeamSlot)
  const visitingImageId = getImageThumbnailId(visitingTeamSlot)

  return (
    <tr
    // className={`${submitLoading || game?.status === 2 ? 'is-disabled' : ''}`}
    >
      <td className='game-number'>
        {game_number}
        {isDirty && (
          <Icon
            name='pencil-alt'
            color='rgba(255, 255, 255, 0.4)'
            css={css`
              position: absolute;
              bottom: calc(50% - 7px);
              left: -24px;
            `}
          />
        )}
      </td>

      <td className={cn('date-picker', { 'is-edited': form?.starts_at })}>
        <DatePicker
          disabled={submitLoading || game?.status === 2 ? true : false}
          hideClear={typeof starts_at === 'string'}
          changeOnReset
          datePlaceholder='Select A Date'
          defaultValue={starts_at}
          onChange={onChange.bind(this, 'starts_at')}
          timezone={selectedVenueObject?.timezone}
          showTimeTDBToggle={true}
          time_to_be_defined_status={
            form?.time_to_be_defined_status !== undefined
              ? form.time_to_be_defined_status
              : detectedTDBStatus
          }
          onTimeTDBChange={(checked) => {
            handleSetForm('time_to_be_defined_status', checked)

            // When toggling TBD, we need to also set starts_at in the form
            // so it gets submitted properly
            if (checked) {
              // Use existing starts_at or current date/time as fallback
              const currentStartsAt =
                form?.starts_at || starts_at || new Date().toISOString()
              handleSetForm('starts_at', setTimeToBeDefined(currentStartsAt))
            } else {
              // When turning TBD off, keep the current starts_at value if it exists
              if (form?.starts_at || starts_at) {
                const currentStartsAt = form?.starts_at || starts_at
                handleSetForm('starts_at', currentStartsAt)
              }
            }
          }}
        />
      </td>

      <td className='venue'>
        <Select
          value={form.venue_id || venue_id || ''}
          onChange={(e) => {
            onChange('venue_id', e)
            onChange('facility_id', { target: { value: '' } })
          }}
          className={cn({ 'is-edited': form?.venue_id })}
          disabled={submitLoading || game?.status === 2 ? true : false}
        >
          <option disabled value=''>
            Select a venue
          </option>
          <option disabled>-----</option>
          {availableVenues.map((venue) => {
            return (
              <option key={venue.id} value={venue.id}>
                {venue.name_full}
              </option>
            )
          })}
        </Select>
      </td>

      <td className='facility'>
        <Select
          value={
            Object.prototype.hasOwnProperty.call(form, 'facility_id')
              ? form.facility_id
              : facility_id || ''
          }
          onChange={onChange.bind(this, 'facility_id')}
          className={cn({ 'is-edited': form?.facility_id })}
          disabled={submitLoading || game?.status === 2 ? true : false}
        >
          <option disabled value=''>
            {selectedVenueObject?.facilities?.length === 0
              ? 'Select venue first'
              : 'Select a facility'}
          </option>
          <option disabled value=''>
            -----
          </option>
          {selectedVenueObject?.facilities?.map((facility) => {
            return (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            )
          })}
        </Select>
      </td>

      <td>
        <SuspensionSubmitButton
          noBorder={true}
          width='16px'
          fontSize='16px'
          busy={advanceLoading === gameId}
          disabled={!selectedVenue || submitLoading || game?.status === 2}
          onClick={() =>
            onPressCopy(
              selectedVenue,
              Object.prototype.hasOwnProperty.call(form, 'facility_id')
                ? form.facility_id
                : facility_id || null
            )
          }
          type='button'
        >
          <Tooltip
            title={'Copy This Line Venue And Facility To All Other Gameslots'}
            placement='top'
            zIndex={11000}
            mouseEnterDelay={1}
          >
            <Icon
              faType='fa'
              name={'clone'}
              color={colors.PRIMARY}
              fontSize={14}
            />
          </Tooltip>
        </SuspensionSubmitButton>
      </td>

      <NewGameTeamSlot
        isEnded={ended_at}
        homeTeamName={homeTeamSlot?.name_full}
        visitingTeamName={visitingTeamSlot?.name_full}
        homeTeamScore={home_team_score}
        visitingTeamScore={visiting_team_score}
        homeImageId={homeImageId}
        visitingImageId={visitingImageId}
        homeDidWin={home_team_score > visiting_team_score}
        visitingDidWin={visiting_team_score > home_team_score}
        disabled={submitLoading || game?.status === 2}
        onPressTeamModal={onPressTeamModal}
        game={game}
      />
      {/* If necessary we can bring the old approach back */}
      {/* <GameSlotTeamCell
        isHome
        teamName={homeTeamSlot?.name_full}
        isEnded={ended_at}
        imageId={homeImageId}
        teamScore={home_team_score}
        didWin={home_team_score > visiting_team_score}
      />
      <GameSlotTeamCell
        teamName={visitingTeamSlot?.name_full}
        isEnded={ended_at}
        imageId={visitingImageId}
        teamScore={visiting_team_score}
        didWin={visiting_team_score > home_team_score}
      /> */}
      <td className='td-center approve'>
        {displayApprovalButton ? (
          <SuspensionSubmitButton
            width='75px'
            fontSize='16px'
            busy={advanceLoading === gameId}
            disabled={!ended_at}
            onClick={onApprove}
            type='button'
          >
            Approve
          </SuspensionSubmitButton>
        ) : status === GAME_SLOT_STATUS.Processed ? (
          <Icon
            faType='fa'
            name={'check'}
            color={colors.PRIMARY}
            fontSize={14}
          />
        ) : (
          '-'
        )}
      </td>
      <td className='td-center round'>{roundName}</td>
      {!isSingleOrDoubleTournament ? (
        <td className='td-center pool'>{poolName}</td>
      ) : null}
      <td className='td-center game-status'>
        {SNGameUID ? (
          <a
            href={`${ROUTES.GAME_ROOT}/${SNGameUID}`}
            target='_blank'
            rel='noreferrer'
            className={cn({ 'is-ended': ended_at })}
          >
            {ended_at || has_bye ? 'Final' : 'Upcoming'}
            <Icon
              name='external-link'
              faType='far'
              fontSize={12}
              css={css`
                margin-left: 6px;
              `}
            />
          </a>
        ) : (
          'Upcoming'
        )}
      </td>
      {IS_DEVELOPMENT && (
        <td className='td-center approve'>
          {advanceButtonsEnabled ? (
            <Flex>
              <SuspensionSubmitButton
                width='60px'
                fontSize='16px'
                busy={advanceLoading === gameId}
                disabled={!homeTeamSlot?.team_id || !visitingTeamSlot?.team_id}
                onClick={onDebugAdvance.bind(this, homeTeamSlot?.team_id)}
                type='button'
              >
                Home
              </SuspensionSubmitButton>
              <SuspensionSubmitButton
                width='60px'
                fontSize='16px'
                busy={advanceLoading === gameId}
                disabled={!homeTeamSlot?.team_id || !visitingTeamSlot?.team_id}
                onClick={onDebugAdvance.bind(this, visitingTeamSlot?.team_id)}
                css={css`
                  margin-left: 8px;
                `}
                type='button'
              >
                Visitor
              </SuspensionSubmitButton>
            </Flex>
          ) : (
            '-'
          )}
        </td>
      )}
    </tr>
  )
}

export default GameSlot
